import { LitElement, PropertyValues, css, html, unsafeCSS, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";

import { api } from "@/lib/openapi";
import { GridStack, styles as gridstackStyles } from "@/lib/gridstack.js";
import { headerManager } from "@/lib/requests";
import "@/wc/dashboard/widget/bpmn-widget.js";
import "@/wc/dashboard/widget/stx/qa-count-per-partner.js";
import "@/wc/dashboard/widget/stx/qa-status.js";
import "@/wc/dashboard/widget/stx/platform-rejected.js";
import "@/wc/dashboard/widget/stx/manual-flag-to-solved.js";
import "@/wc/dashboard/widget/stx/financial-statistics.js";
import "@/wc/dashboard/widget/stx/outliers.js";

import bulmaStyles from "bulma/css/bulma.min.css?inline";
import fontAwesomeA from "@fortawesome/fontawesome-free/css/fontawesome.min.css?inline";
import fontAwesomeB from "@fortawesome/fontawesome-free/css/solid.css?inline";

const rowsInGrid = 6;
const marginBetweenWidgets = 10;
const widthPerDashboardBlock = 120;

// This widget base is temporarily used until we have a proper widget system
// in place. This widget allows you to 'wrap' other widgets (or plain html),
// and provides an edit button so that you can reconfigure whatever it wraps.
// (e.g. it might wrap html that looks like '<c-bpmn-widget data-process-id="nanoid"></c-bpmn-widget>')
@customElement("c-temp-widget-base")
export class WidgetComponent extends LitElement {
    static styles = [
        unsafeCSS(fontAwesomeA + fontAwesomeB),
        unsafeCSS(bulmaStyles),
        css`
            :host > div {
                height: 100%;
            }

            .toolbar {
                position: absolute;
                top: 0;
                right: 0;
                z-index: 1;
            }

            .button.is-primary {
                background-color: var(--stekz-primary-color);
            }
            .button.is-primary:hover {
                background-color: var(--stekz-blue-darker);
            }

            .form {
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        `,
    ];

    @property({ attribute: "data-index" })
    index: number;

    @property({ attribute: "data-allow-edit" })
    allowEditing: boolean = false;

    @state()
    isEditing = false;

    @state()
    showModal = false;

    slotRef: Ref<HTMLSlotElement> = createRef();

    @state()
    copyOfWidgetContent: string;

    toolbarTemplate() {
        if (!this.allowEditing) {
            return nothing;
        }

        return html`<div class="toolbar">
            ${this.isEditing
                ? html`
                      <button
                          class="button is-primary"
                          @click=${async () => {
                              this.isEditing = false;
                              this.dispatchEvent(
                                  new CustomEvent("widget:stopped-editing", {
                                      bubbles: true,
                                      composed: true,
                                      detail: {
                                          html: this.copyOfWidgetContent,
                                          index: this.index,
                                      },
                                  })
                              );
                          }}
                      >
                          <span class="icon">
                              <i class="fas fa-save"></i>
                          </span>
                          <span>Save</span>
                      </button>
                  `
                : html` <button
                      class="button is-primary"
                      @click=${() => {
                          this.isEditing = true;
                          this.dispatchEvent(
                              new CustomEvent("widget:started-editing", {
                                  bubbles: true,
                                  composed: true,
                              })
                          );
                      }}
                  >
                      <span class="icon">
                          <i class="fas fa-edit"></i>
                      </span>
                      <span>Edit</span>
                  </button>`}
        </div>`;
    }

    editTemplate() {
        return html`<div class="form">
            <input
                value=${this.copyOfWidgetContent}
                @input=${(e) => {
                    this.copyOfWidgetContent = e.target.value;
                }}
            ></input>
        </div>`;
    }

    viewTemplate() {
        return html`<slot ${ref(this.slotRef)}></slot>`;
    }

    render() {
        return html`<div>
            ${this.toolbarTemplate()}
            ${this.isEditing ? this.editTemplate() : this.viewTemplate()}
        </div>`;
    }

    // Wait until rendering is complete (through this.updateComplete),
    // then copy the widget content from the slot component to a variable.
    async copyWidgetContent() {
        await this.updateComplete;
        this.copyOfWidgetContent = this.slotRef.value.assignedElements({
            flatten: true,
        })[0].innerHTML;

        console.log(this.copyOfWidgetContent);
    }

    connectedCallback() {
        super.connectedCallback();
        this.copyWidgetContent();
    }
}

interface Widget {
    x: number;
    y: number;
    w?: number;
    h?: number;
    content: string;
}

const parser = new DOMParser();
// Removes the 'base widget' part from the widget content. A widget
// that looks like:
// <c-temp-widget-base>
//    <!-- lit comment -->
//    <div class="widget-wrapper">
//        <c-bpmn-widget data-process-id="nanoid"></c-bpmn-widget>
//    </div>
// </c-temp-widget-base>
//
// Will be transformed to:
// <c-bpmn-widget data-process-id="nanoid"></c-bpmn-widget>
function cleanWidget(widget: Widget) {
    let content = widget.content;
    if (content.includes("c-temp-widget-base")) {
        const widgetFragment = parser.parseFromString(content, "text/html");
        content = widgetFragment.querySelector(
            "c-temp-widget-base > div.widget-wrapper"
        ).innerHTML;
    }
    // Strip all html comments (Lit uses them to track nodes)
    content = content.replace(/<!--.*?-->/g, "");

    return { ...widget, w: widget.w ?? 1, h: widget.h ?? 1, content };
}

// The Dashboard component manages Gridstack.js, and wraps multiple Widgets.
// Each of those widgets can be configured by the user, as they are wrapped
// in a 'widget base' component which provides widget configurability.
@customElement("c-dashboard")
export class DashboardComponent extends LitElement {
    static styles = [
        unsafeCSS(gridstackStyles),
        unsafeCSS(bulmaStyles),
        unsafeCSS(fontAwesomeA + fontAwesomeB),
        css`
            :host {
                display: flex;
                flex-wrap: wrap;
            }

            .toolbar {
                display: flex;
                flex: 1;
                flex-direction: column;
            }

            .grid-stack-wrapper {
                flex: 6;
            }

            .grid-stack {
                background-color: #eee;
            }

            .grid-stack-item-content {
                background-color: #fff;
            }

            #trash {
                display: flex;
                flex: 0;
                min-height: calc(
                    100% / ${rowsInGrid * 2} + ${marginBetweenWidgets}px
                );
                background-color: rgb(255, 97, 97);
                justify-content: space-around;
                align-items: center;
            }

            .grid-stack-item-removing {
                opacity: 0.8;
                filter: blur(5px);
            }

            .widgets {
                flex: 1;
                border-right: 1px solid #ccc;
            }

            .widgets li {
                border-bottom: 1px solid #ccc;
                text-align: center;
            }

            .widget-wrapper {
                width: 100%;
                height: 100%;
            }
        `,
    ];

    items: Widget[];

    gridRef: Ref<HTMLDivElement> = createRef();
    grid: GridStack;

    dashboardId: string;

    @property({ attribute: "data-edit" })
    isEditing?: boolean = false;

    @property({ attribute: "data-show-widget-toolbar" })
    shouldShowWidgetToolbar?: boolean = false;

    // This is called whenever a property is updated. We use this to update
    // the gridstack instance since its lifecycle is not managed by lit
    // directly.
    willUpdate(changedProperties: PropertyValues<this>) {
        if (!this.grid) {
            return;
        }

        if (changedProperties.has("isEditing")) {
            this.setStaticAfterDomUpdate();
            this.updateWidgetsConfigurability();
        }

        if (changedProperties.has("shouldShowWidgetToolbar")) {
            this.setStaticAfterDomUpdate().then(() => {
                // We have to toggle the remove drop manually as it
                // doesn't get toggled when setStatic is called with the
                // same value multiple times. This configures the listeners
                // required to make the 'drag widget to trash' functionality
                // work.
                this.grid._setupRemoveDrop();

                // There an error thrown whenever you try to add something
                // to a grid that is already full. Something to solve
                // later.
                GridStack.setupDragIn(
                    Array.from(
                        this.renderRoot.querySelectorAll(".widgets li > div")
                    ),
                    { helper: "clone" }
                );
            });
        }
    }

    // Toggles the grid to be static (or not) after a DOM update, depending
    // on whether the dashboard is in edit mode or not. When the grid is
    // static, the widgets cannot be moved around.
    async setStaticAfterDomUpdate() {
        await this.updateComplete;
        this.grid.setStatic(!this.isEditing);
    }

    // Adds or removes the 'data-allow-edit' attribute for all widgets depending
    // on whether the dashboard is in edit mode or not.
    async updateWidgetsConfigurability() {
        await this.updateComplete;
        Array.from(
            this.gridRef.value.querySelectorAll("c-temp-widget-base")
        ).forEach((widget: WidgetComponent) => {
            if (this.isEditing) {
                widget.setAttribute("data-allow-edit", "true");
            } else {
                widget.removeAttribute("data-allow-edit");
            }
        });
    }

    // Saves the widgets to the backend. If no widgets are provided, the
    // current state of the grid is saved.
    async save(widgets: Widget[]) {
        const updatedWidgets = widgets ?? this.grid.save();
        console.log({ updatedWidgets });
        const { response } =
            await api.startDashboardDashboardMgtUpdateDashboardStartPost({
                body: {
                    dashboard_id: this.dashboardId,
                    widgets: updatedWidgets.map((widget) =>
                        cleanWidget(widget)
                    ),
                },
            });

        if (response.ok) {
            this.items = updatedWidgets as Widget[];
        }
    }

    // Renders a supported widget type. Later on we can extend this to show
    // different icons per widget type.
    widgetTypeTemplate(widgetType) {
        return html`<li>
            <div class="grid-stack-item">
                <div class="grid-stack-item-content">${widgetType}</div>
            </div>
        </li>`;
    }

    // Renders a list of supported widget types on the left side of the
    // screen, when the widget toolbar is enabled.
    widgetToolbarTemplate() {
        const widgetTypes = ["html"];

        return html`<div class="toolbar">
            <div class="widgets">
                <ul>
                    ${widgetTypes.map((widgetType) =>
                        this.widgetTypeTemplate(widgetType)
                    )}
                </ul>
            </div>
            <div id="trash" class="ui-droppable ui-droppable-over">
                <span class="icon is-large">
                    <span class="fa-trash fa-lg">
                        <i class="fas fa-camera fa-stack-1x"></i>
                    </span>
                </span>
            </div>
        </div>`;
    }

    render() {
        return html` ${this.shouldShowWidgetToolbar
                ? this.widgetToolbarTemplate()
                : nothing}
            <div class="grid-stack-wrapper">
                <div class="grid-stack" ${ref(this.gridRef)}></div>
            </div>`;
    }

    async retrieveWidgets() {
        // Have to wait until the Vue organization managers are done setting
        // the right headers before we fire any requests.
        await headerManager.allHeadersSet;
        const { data } =
            await api.startDashboardDashboardMgtGetDashboardsByStekStartPost(
                {}
            );

        const dashboard = data?.find(
            (dashboard) => dashboard.name === "default"
        );
        if (!dashboard) {
            return [];
        }

        this.dashboardId = dashboard.identity;

        return dashboard.widgets?.map((item, index) => ({
            ...item,
            content: `<c-temp-widget-base data-index=${index}><div class="widget-wrapper">${item.content}</div></c-temp-widget-base>`,
        }));
    }

    // Initializes the gridstack instance and loads the widgets from the
    // backend.
    async firstUpdated() {
        this.grid = GridStack.init(
            {
                cellHeight: "auto",
                acceptWidgets: true,
                removable: "#trash",
                minRow: rowsInGrid,
                maxRow: rowsInGrid,
                margin: marginBetweenWidgets,
                columnOpts: {
                    columnWidth: widthPerDashboardBlock,
                    layout: "list",
                },
            },
            // @ts-expect-error: TS2345 The typings of GridStack are incorrect, they actually support passing a node.
            this.gridRef.value
        );
        this.grid.setStatic(!this.isEditing);
        this.items = await this.retrieveWidgets();
        this.grid.load(this.items);
        this.updateWidgetsConfigurability();
    }

    // Update the items array, save it to the backend, and reload the grid.
    async saveWidgetConfiguration(index: number, content: string) {
        console.log({ index, content });
        this.items[index].content = content;
        await this.save(this.items);
        this.items = await this.retrieveWidgets();
        this.grid.removeAll();
        this.grid.load(this.items);
        this.updateWidgetsConfigurability();
    }

    async handleEvent(e) {
        switch (e.type) {
            case "widget:started-editing":
                break;
            case "widget:stopped-editing":
                this.saveWidgetConfiguration(e.detail.index, e.detail.html);
                break;
        }
    }

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener("widget:started-editing", this);
        this.addEventListener("widget:stopped-editing", this);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.removeEventListener("widget:started-editing", this);
        this.removeEventListener("widget:stopped-editing", this);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-dashboard": DashboardComponent;
        "c-temp-widget-base": WidgetComponent;
    }
}
